import React from 'react';
import PropTypes from 'prop-types';
import { intlShape } from '../../util/reactIntl';
import routeConfiguration from '../../routeConfiguration';
import { LISTING_STATE_DRAFT, LISTING_STATE_PENDING_APPROVAL } from '../../util/types';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_NEW,
  LISTING_PAGE_PARAM_TYPES,
  createSlug,
} from '../../util/urlHelpers';
import { ensureListing } from '../../util/data';
import { createResourceLocatorString } from '../../util/routes';
import {
  EditListingAvailabilityPanel,
  EditListingDescriptionPanel,
  EditListingWifiSpeedPanel,
  EditListingWorkspacePanel,
  EditListingFeaturesPanel,
  EditListingLocationPanel,
  EditListingPhotosPanel,
  EditListingPoliciesPanel,
  EditListingPricingPanel,
  EditListingPublishPanel,
} from '../../components';

import css from './EditListingWizard.module.css';

export const AVAILABILITY = 'availability';
export const DESCRIPTION = 'description';
export const WIFI_SPEED = 'wifiSpeed';
export const WORKSPACE = 'workspace';
export const FEATURES = 'features';
export const POLICY = 'policy';
export const LOCATION = 'location';
export const PUBLISH = 'publish';
export const PRICING = 'pricing';
export const PHOTOS = 'photos';

// EditListingWizardTab component supports these tabs
export const SUPPORTED_TABS = [
  DESCRIPTION,
  WIFI_SPEED,
  WORKSPACE,
  FEATURES,
  POLICY,
  LOCATION,
  PUBLISH,
  PRICING,
  AVAILABILITY,
  PHOTOS,
];

const pathParamsToNextTab = (params, tab, marketplaceTabs) => {
  const nextTabIndex = marketplaceTabs.findIndex(s => s === tab) + 1;
  const nextTab =
    nextTabIndex < marketplaceTabs.length
      ? marketplaceTabs[nextTabIndex]
      : marketplaceTabs[marketplaceTabs.length - 1];
  return { ...params, tab: nextTab };
};

const pathParamsToPrevTab = (params, tab, marketplaceTabs) => {
  const prevTabIndex = marketplaceTabs.findIndex(s => s === tab) - 1;
  const prevTab = prevTabIndex >= 0 ? marketplaceTabs[prevTabIndex] : marketplaceTabs[0];
  return { ...params, tab: prevTab };
};

// When user has update draft listing, he should be redirected to next EditListingWizardTab
const redirectAfterDraftUpdate = (listingId, params, tab, marketplaceTabs, history) => {
  const currentPathParams = {
    ...params,
    type: LISTING_PAGE_PARAM_TYPE_DRAFT,
    id: listingId,
  };
  const routes = routeConfiguration();

  // Replace current "new" path to "draft" path.
  // Browser's back button should lead to editing current draft instead of creating a new one.
  if (params.type === LISTING_PAGE_PARAM_TYPE_NEW) {
    const draftURI = createResourceLocatorString('EditListingPage', routes, currentPathParams, {});
    history.replace(draftURI);
  }

  // Redirect to next tab
  const nextPathParams = pathParamsToNextTab(currentPathParams, tab, marketplaceTabs);
  const to = createResourceLocatorString('EditListingPage', routes, nextPathParams, {});
  history.push(to);
};

const createListingURL = (routes, listing) => {
  const id = listing.id.uuid;
  const slug = createSlug(listing.attributes.title);
  const isPendingApproval =
      listing.attributes.state === LISTING_STATE_PENDING_APPROVAL;
  const isDraft = listing.attributes.state === LISTING_STATE_DRAFT;
  const variant = isDraft
      ? LISTING_PAGE_DRAFT_VARIANT
      : isPendingApproval
      ? LISTING_PAGE_PENDING_APPROVAL_VARIANT
      : null;

  const linkProps =
      isPendingApproval || isDraft
          ? {
                name: 'ListingPageVariant',
                params: {
                    id,
                    slug,
                    variant,
                },
            }
          : {
                name: 'ListingPage',
                params: { id, slug },
            };

  return createResourceLocatorString(
      linkProps.name,
      routes,
      linkProps.params,
      {}
  );
};

const EditListingWizardTab = props => {
  const {
    tab,
    marketplaceTabs,
    params,
    errors,
    fetchInProgress,
    newListingPublished,
    history,
    images,
    availability,
    listing,
    handleCreateFlowTabScrolling,
    handlePublishListing,
    onUpdateListing,
    onCreateListingDraft,
    onImageUpload,
    onUpdateImageOrder,
    onRemoveImage,
    onChange,
    updatedTab,
    updateInProgress,
    intl,
    onManageDisableScrolling
  } = props;

  const { type } = params;
  const isNewURI = type === LISTING_PAGE_PARAM_TYPE_NEW;
  const isDraftURI = type === LISTING_PAGE_PARAM_TYPE_DRAFT;
  const isNewListingFlow = isNewURI || isDraftURI;

  const currentListing = ensureListing(listing);
  const imageIds = images => {
    return images ? images.map(img => img.imageId || img.id) : null;
  };

  //Redirect to prev tab
  const redirectAfterPrevTab = (params, tab, marketplaceTabs) => {
    const prevPathParams = pathParamsToPrevTab(params, tab, marketplaceTabs);
    const routes = routeConfiguration();
    const to = createResourceLocatorString('EditListingPage', routes, prevPathParams, {});
    history.push(to);
  };

  const onCompleteEditListingWizardTab = (tab, updateValues) => {
    // Normalize images for API call
    const { images: updatedImages, ...otherValues } = updateValues;
    const imageProperty =
      typeof updatedImages !== 'undefined' ? { images: imageIds(updatedImages) } : {};
    const updateValuesWithImages = { ...otherValues, ...imageProperty };

    if (isNewListingFlow) {
      const onUpsertListingDraft = isNewURI
        ? (tab, updateValues) => onCreateListingDraft(updateValues)
        : onUpdateListing;

      const upsertValues = isNewURI
        ? updateValuesWithImages
        : { ...updateValuesWithImages, id: currentListing.id };

      onUpsertListingDraft(tab, upsertValues)
        .then(r => {
          if (tab !== marketplaceTabs[marketplaceTabs.length - 1]) {
            // Create listing flow: smooth scrolling polyfill to scroll to correct tab
            handleCreateFlowTabScrolling(false);

            // After successful saving of draft data, user should be redirected to next tab
            redirectAfterDraftUpdate(r.data.data.id.uuid, params, tab, marketplaceTabs, history);
          } else {
            handlePublishListing(currentListing.id);
          }
        })
        .catch(e => {
          // No need for extra actions
        });
    } else {
      onUpdateListing(tab, { ...updateValuesWithImages, id: currentListing.id });
    }
  };

  const onSaveImages = (tab, updateValues) => {
    // Normalize images for API call
    const { images: updatedImages, ...otherValues } = updateValues;
    const imageProperty =
      typeof updatedImages !== 'undefined' ? { images: imageIds(updatedImages) } : {};
    const updateValuesWithImages = { ...otherValues, ...imageProperty };

    if (isNewListingFlow) {
      const onUpsertListingDraft = isNewURI
        ? (tab, updateValues) => onCreateListingDraft(updateValues)
        : onUpdateListing;

      const upsertValues = isNewURI
        ? updateValuesWithImages
        : { ...updateValuesWithImages, id: currentListing.id };

      onUpsertListingDraft(tab, upsertValues)
        .then(r => {
          if (tab !== marketplaceTabs[marketplaceTabs.length - 1]) {
            // Create listing flow: smooth scrolling polyfill to scroll to correct tab
            handleCreateFlowTabScrolling(false);
          } else {
            handlePublishListing(currentListing.id);
          }
        })
        .catch(e => {
          // No need for extra actions
        });
    } else {
      onUpdateListing(tab, { ...updateValuesWithImages, id: currentListing.id });
    }
  };

  const panelProps = tab => {
    return {
      className: css.panel,
      errors,
      listing,
      onChange,
      panelUpdated: updatedTab === tab,
      updateInProgress,
      // newListingPublished and fetchInProgress are flags for the last wizard tab
      ready: newListingPublished,
      disabled: fetchInProgress,
    };
  };

  switch (tab) {
    case DESCRIPTION: {
      const submitButtonTranslationKey = isNewListingFlow
        ? 'EditListingWizard.saveNewDescription'
        : 'EditListingWizard.saveEditDescription';
      return (
        <EditListingDescriptionPanel
          {...panelProps(DESCRIPTION)}
          submitButtonText={intl.formatMessage({ id: submitButtonTranslationKey })}
          backButtonText={intl.formatMessage({ id: 'EditListingWizard.backButton' })}
          backToListing={() => {
            history.push(
              createListingURL(routeConfiguration(), listing)
            );
          }}
          backToListingText={intl.formatMessage({
            id: 'EditListingWizard.backToListing',
          })}
          isNewListingFlow={isNewListingFlow}
          onSubmit={values => {
            onCompleteEditListingWizardTab(tab, values);
          }}
        />
      );
    }
    case WIFI_SPEED: {
      const submitButtonTranslationKey = isNewListingFlow
        ? 'EditListingWizard.saveNewWifiSpeed'
        : 'EditListingWizard.saveEditWifiSpeed';
      return (
        <EditListingWifiSpeedPanel
          {...panelProps(WIFI_SPEED)}
          submitButtonText={intl.formatMessage({ id: submitButtonTranslationKey })}
          backButtonText={intl.formatMessage({ id: 'EditListingWizard.backButton' })}
          onSubmit={values => {
            onCompleteEditListingWizardTab(tab, values);
          }}
          onManageDisableScrolling={onManageDisableScrolling}
          redirectAfterPrevTab={() => redirectAfterPrevTab(params, tab, marketplaceTabs)}
          backToListing={() => {
            history.push(
              createListingURL(routeConfiguration(), listing)
            );
          }}
          backToListingText={intl.formatMessage({
            id: 'EditListingWizard.backToListing',
          })}
          isNewListingFlow={isNewListingFlow}
        />
      );
    }
    case WORKSPACE: {
      const submitButtonTranslationKey = isNewListingFlow
        ? 'EditListingWizard.saveNewFeatures'
        : 'EditListingWizard.saveEditFeatures';
      return (
        <EditListingWorkspacePanel
          {...panelProps(WORKSPACE)}
          submitButtonText={intl.formatMessage({ id: submitButtonTranslationKey })}
          backButtonText={intl.formatMessage({ id: 'EditListingWizard.backButton' })}
          onSubmit={values => {
            onCompleteEditListingWizardTab(tab, values);
          }}
          onManageDisableScrolling={onManageDisableScrolling}
          redirectAfterPrevTab={() => redirectAfterPrevTab(params, tab, marketplaceTabs)}
          backToListing={() => {
            history.push(
              createListingURL(routeConfiguration(), listing)
            );
          }}
          backToListingText={intl.formatMessage({
            id: 'EditListingWizard.backToListing',
          })}
          isNewListingFlow={isNewListingFlow}
        />
      );
    }
    case FEATURES: {
      const submitButtonTranslationKey = isNewListingFlow
        ? 'EditListingWizard.saveNewFeatures'
        : 'EditListingWizard.saveEditFeatures';
      return (
        <EditListingFeaturesPanel
          {...panelProps(FEATURES)}
          submitButtonText={intl.formatMessage({ id: submitButtonTranslationKey })}
          backButtonText={intl.formatMessage({ id: 'EditListingWizard.backButton' })}
          onSubmit={values => {
            onCompleteEditListingWizardTab(tab, values);
          }}
          redirectAfterPrevTab={() => redirectAfterPrevTab(params, tab, marketplaceTabs)}
          backToListing={() => {
            history.push(
              createListingURL(routeConfiguration(), listing)
            );
          }}
          backToListingText={intl.formatMessage({
            id: 'EditListingWizard.backToListing',
          })}
          isNewListingFlow={isNewListingFlow}
        />
      );
    }
    case POLICY: {
      const submitButtonTranslationKey = isNewListingFlow
        ? 'EditListingWizard.saveNewPolicies'
        : 'EditListingWizard.saveEditPolicies';
      return (
        <EditListingPoliciesPanel
          {...panelProps(POLICY)}
          submitButtonText={intl.formatMessage({ id: submitButtonTranslationKey })}
          backButtonText={intl.formatMessage({ id: 'EditListingWizard.backButton' })}
          onSubmit={values => {
            onCompleteEditListingWizardTab(tab, values);
          }}
          onSubmitImages={values => {
            onSaveImages(tab, values);
          }}
          redirectAfterPrevTab={() => redirectAfterPrevTab(params, tab, marketplaceTabs)}
          backToListing={() => {
            history.push(
              createListingURL(routeConfiguration(), listing)
            );
          }}
          backToListingText={intl.formatMessage({
            id: 'EditListingWizard.backToListing',
          })}
          isNewListingFlow={isNewListingFlow}
        />
      );
    }
    case LOCATION: {
      const submitButtonTranslationKey = isNewListingFlow
        ? 'EditListingWizard.saveNewLocation'
        : 'EditListingWizard.saveEditLocation';
      return (
        <EditListingLocationPanel // This panel is for the surroundings (Tips for the host)
          {...panelProps(LOCATION)}
          submitButtonText={intl.formatMessage({ id: submitButtonTranslationKey })}
          backButtonText={intl.formatMessage({ id: 'EditListingWizard.backButton' })}
          onSubmit={values => {
            onCompleteEditListingWizardTab(tab, values);
          }}
          onSubmitImages={values => {
            onSaveImages(tab, values);
          }}
          redirectAfterPrevTab={() => redirectAfterPrevTab(params, tab, marketplaceTabs)}
          backToListing={() => {
            history.push(
              createListingURL(routeConfiguration(), listing)
            );
          }}
          backToListingText={intl.formatMessage({
            id: 'EditListingWizard.backToListing',
          })}
          isNewListingFlow={isNewListingFlow}
        />
      );
    }
    case PUBLISH: {
      const submitButtonTranslationKey = isNewListingFlow
        ? 'EditListingWizard.saveNewPublish'
        : 'EditListingWizard.saveEditPublish';
      return (
        <EditListingPublishPanel // This panel is for the surroundings (Tips for the host)
          {...panelProps(PUBLISH)}
          submitButtonText={intl.formatMessage({ id: submitButtonTranslationKey })}
          backButtonText={intl.formatMessage({ id: 'EditListingWizard.backButton' })}
          onSubmit={values => {
            onCompleteEditListingWizardTab(tab, values);
          }}
          params={params}
          redirectAfterPrevTab={() => redirectAfterPrevTab(params, tab, marketplaceTabs)}
          backToListing={() => {
            history.push(
              createListingURL(routeConfiguration(), listing)
            );
          }}
          backToListingText={intl.formatMessage({
            id: 'EditListingWizard.backToListing',
          })}
          isNewListingFlow={isNewListingFlow}
        />
      );
    }
    case PRICING: {
      const submitButtonTranslationKey = isNewListingFlow
        ? 'EditListingWizard.saveNewPricing'
        : 'EditListingWizard.saveEditPricing';
      return (
        <EditListingPricingPanel
          {...panelProps(PRICING)}
          submitButtonText={intl.formatMessage({ id: submitButtonTranslationKey })}
          backButtonText={intl.formatMessage({ id: 'EditListingWizard.backButton' })}
          onSubmit={values => {
            onCompleteEditListingWizardTab(tab, values);
          }}
          redirectAfterPrevTab={() => redirectAfterPrevTab(params, tab, marketplaceTabs)}
          backToListing={() => {
            history.push(
              createListingURL(routeConfiguration(), listing)
            );
          }}
          backToListingText={intl.formatMessage({
            id: 'EditListingWizard.backToListing',
          })}
          isNewListingFlow={isNewListingFlow}
        />
      );
    }
    case AVAILABILITY: {
      const submitButtonTranslationKey = isNewListingFlow
        ? 'EditListingWizard.saveNewAvailability'
        : 'EditListingWizard.saveEditAvailability';
      return (
        <EditListingAvailabilityPanel
          {...panelProps(AVAILABILITY)}
          availability={availability}
          submitButtonText={intl.formatMessage({ id: submitButtonTranslationKey })}
          backButtonText={intl.formatMessage({ id: 'EditListingWizard.backButton' })}
          onSubmit={values => {
            onCompleteEditListingWizardTab(tab, values);
          }}
          redirectAfterPrevTab={() => redirectAfterPrevTab(params, tab, marketplaceTabs)}
          backToListing={() => {
            history.push(
              createListingURL(routeConfiguration(), listing)
            );
          }}
          backToListingText={intl.formatMessage({
            id: 'EditListingWizard.backToListing',
          })}
          isNewListingFlow={isNewListingFlow}
        />
      );
    }
    case PHOTOS: {
      const submitButtonTranslationKey = isNewListingFlow
        ? 'EditListingWizard.saveNewPhotos'
        : 'EditListingWizard.saveEditPhotos';

      return (
        <EditListingPhotosPanel
          {...panelProps(PHOTOS)}
          submitButtonText={intl.formatMessage({ id: submitButtonTranslationKey })}
          backButtonText={intl.formatMessage({ id: 'EditListingWizard.backButton' })}
          images={images}
          onImageUpload={onImageUpload}
          onRemoveImage={onRemoveImage}
          onSubmit={values => {
            onCompleteEditListingWizardTab(tab, values);
          }}
          onUpdateImageOrder={onUpdateImageOrder}
          redirectAfterPrevTab={() => redirectAfterPrevTab(params, tab, marketplaceTabs)}
          backToListing={() => {
            history.push(
              createListingURL(routeConfiguration(), listing)
            );
          }}
          backToListingText={intl.formatMessage({
            id: 'EditListingWizard.backToListing',
          })}
          isNewListingFlow={isNewListingFlow}
        />
      );
    }
    default:
      return null;
  }
};

EditListingWizardTab.defaultProps = {
  listing: null,
  updatedTab: null,
};

const { array, bool, func, object, oneOf, shape, string } = PropTypes;

EditListingWizardTab.propTypes = {
  params: shape({
    id: string.isRequired,
    slug: string.isRequired,
    type: oneOf(LISTING_PAGE_PARAM_TYPES).isRequired,
    tab: oneOf(SUPPORTED_TABS).isRequired,
  }).isRequired,
  errors: shape({
    createListingDraftError: object,
    publishListingError: object,
    updateListingError: object,
    showListingsError: object,
    uploadImageError: object,
  }).isRequired,
  fetchInProgress: bool.isRequired,
  newListingPublished: bool.isRequired,
  history: shape({
    push: func.isRequired,
    replace: func.isRequired,
  }).isRequired,
  images: array.isRequired,
  availability: object.isRequired,

  // We cannot use propTypes.listing since the listing might be a draft.
  listing: shape({
    attributes: shape({
      publicData: object,
      description: string,
      geolocation: object,
      pricing: object,
      title: string,
    }),
    images: array,
  }),

  handleCreateFlowTabScrolling: func.isRequired,
  handlePublishListing: func.isRequired,
  onUpdateListing: func.isRequired,
  onCreateListingDraft: func.isRequired,
  onImageUpload: func.isRequired,
  onUpdateImageOrder: func.isRequired,
  onRemoveImage: func.isRequired,
  onChange: func.isRequired,
  updatedTab: string,
  updateInProgress: bool.isRequired,

  intl: intlShape.isRequired,
};

export default EditListingWizardTab;
